import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import type { InvitationPreview, Organisation } from "@/lib/types";
import { Button } from "@/ui/Button";
import { AuthCard, FormError } from "@/features/auth/AuthCard";
import { Skeleton } from "@/ui/States";
import { absoluteTime } from "@/lib/format";
import { ROLE_LABELS } from "@/features/org/roles";

export const Route = createFileRoute("/_public/invitation")({
  component: InvitationScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

/**
 * Accepting an invitation, for someone who may not have an account yet.
 *
 * The preview is public so a recipient can see what they are joining before
 * they are asked to register. Accepting requires being signed in as the
 * addressee, which the API enforces.
 */
function InvitationScreen() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const { user, refresh } = useSession();

  const preview = useQuery({
    queryKey: ["invitation", token],
    queryFn: ({ signal }) =>
      api.get<{ invitation: InvitationPreview }>("/invitations", { token }, signal),
    enabled: Boolean(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => api.post<{ organisation: Organisation }>("/invitations/accept", { token }),
    onSuccess: async (result) => {
      await refresh();
      await router.invalidate();
      await navigate({ to: "/o/$orgId", params: { orgId: result.organisation.id } });
    },
  });

  if (!token) {
    return (
      <AuthCard title="This invitation link is incomplete">
        <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
          Open the link from your invitation email again, or ask whoever invited you to send a new
          one.
        </p>
      </AuthCard>
    );
  }

  if (preview.isLoading) {
    return (
      <AuthCard title="Invitation">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-9 w-full" />
        </div>
      </AuthCard>
    );
  }

  if (preview.isError) {
    return (
      <AuthCard
        title="This invitation is not available"
        footer={
          <Link to="/sign-in" className="font-medium text-[var(--primary)] hover:underline">
            Go to sign in
          </Link>
        }
      >
        <FormError error={preview.error} />
      </AuthCard>
    );
  }

  const invitation = preview.data!.invitation;
  const roleLabel = ROLE_LABELS[invitation.role as keyof typeof ROLE_LABELS] ?? invitation.role;
  const signedInAsSomeoneElse =
    user !== null && user.email.toLowerCase() !== invitation.email.toLowerCase();

  return (
    <AuthCard
      title={`Join ${invitation.organisation_name}`}
      subtitle={
        invitation.invited_by_email
          ? `${invitation.invited_by_email} invited you as a ${roleLabel.toLowerCase()}.`
          : `You have been invited as a ${roleLabel.toLowerCase()}.`
      }
    >
      <div className="flex flex-col gap-4">
        <dl className="flex flex-col gap-2 text-[13px]">
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--text-muted)]">Invited address</dt>
            <dd className="mono text-[var(--text-primary)]">{invitation.email}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--text-muted)]">Role</dt>
            <dd className="text-[var(--text-primary)]">{roleLabel}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--text-muted)]">Expires</dt>
            <dd className="text-[var(--text-primary)]">{absoluteTime(invitation.expires_at)}</dd>
          </div>
        </dl>

        {accept.error ? <FormError error={accept.error} /> : null}

        {!user && (
          <>
            <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
              Sign in as <span className="mono">{invitation.email}</span> to accept, or create an
              account with that address first.
            </p>
            <div className="flex gap-2">
              <Link to="/sign-in" className="flex-1">
                <Button tone="primary" wide>
                  Sign in
                </Button>
              </Link>
              <Link to="/register" className="flex-1">
                <Button tone="secondary" wide>
                  Create account
                </Button>
              </Link>
            </div>
          </>
        )}

        {signedInAsSomeoneElse && (
          <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
            You are signed in as <span className="mono">{user!.email}</span>. This invitation was
            sent to <span className="mono">{invitation.email}</span>, so it can only be accepted by
            that account.
          </p>
        )}

        {user && !signedInAsSomeoneElse && (
          <Button
            tone="primary"
            wide
            busy={accept.isPending}
            onClick={() => accept.mutate()}
          >
            Accept invitation
          </Button>
        )}
      </div>
    </AuthCard>
  );
}
